import React, { useEffect, useRef, useState } from 'react';
import algoliasearch from 'algoliasearch/lite';
import {
  ClearRefinements,
  Configure,
  InstantSearch,
  RefinementList,
  SearchBox,
  SortBy,
  Stats,
  useInfiniteHits,
  useRange
} from 'react-instantsearch';
import { history } from 'instantsearch.js/es/lib/routers';
import { decodeHtml } from '../../lib/utilities';

const appId = wlAlgolia.application_id;
const apiKey = wlAlgolia.search_api_key;
const indexName =
  (window.wlRecipeArchiveConfig || {}).indexName ||
  wlAlgolia.indices.searchable_posts.name;
const searchClient = algoliasearch(appId, apiKey);

function trackRecipeArchiveSearch(query) {
  const searchTerm = typeof query === 'string' ? query.trim() : '';

  if (!searchTerm) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'recipe_archive_search',
    search_term: searchTerm,
    search_surface: 'recipe_archive',
    page_url: window.location.href
  });
}

const queryHook = (query, search) => {
  search(query);
  trackRecipeArchiveSearch(query);
};

const routing = {
  router: history(),
  stateMapping: {
    stateToRoute(uiState) {
      const indexUiState = uiState[indexName] || {};

      return {
        s: indexUiState.query,
        sortBy: indexUiState.sortBy,
        cooking_methods: indexUiState.refinementList
          ? indexUiState.refinementList.cooking_methods
          : [],
        recipe_categories: indexUiState.refinementList
          ? indexUiState.refinementList.recipe_categories
          : [],
        cook_time_minutes: indexUiState.range
          ? indexUiState.range.cook_time_minutes
          : undefined,
        ingredient_count: indexUiState.range
          ? indexUiState.range.ingredient_count
          : undefined
      };
    },
    routeToState(routeState) {
      return {
        [indexName]: {
          query: routeState.s,
          refinementList: {
            cooking_methods: routeState.cooking_methods || [],
            recipe_categories: routeState.recipe_categories || []
          },
          range: {
            cook_time_minutes: routeState.cook_time_minutes,
            ingredient_count: routeState.ingredient_count
          },
          sortBy: routeState.sortBy
        }
      };
    }
  }
};

function parseCookTimeMinutes(value) {
  if (typeof value === 'undefined' || value === null) {
    return null;
  }

  const duration = String(value).trim().toLowerCase();

  if (!duration) {
    return null;
  }

  const hoursMatch = duration.match(/(\d+(?:\.\d+)?)\s*(h|hr|hrs|hour|hours)/);
  const minutesMatch = duration.match(/(\d+)\s*(m|min|mins|minute|minutes)/);
  const numericMatch = duration.match(/^(\d+)$/);

  if (!hoursMatch && !minutesMatch && !numericMatch) {
    return null;
  }

  const hours = hoursMatch ? Number(hoursMatch[1]) : 0;
  const minutes = minutesMatch ? Number(minutesMatch[1]) : 0;

  return Math.round(
    hours * 60 + minutes + (numericMatch ? Number(numericMatch[1]) : 0)
  );
}

function formatDuration(minutes) {
  const totalMinutes = Math.round(Number(minutes));

  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) {
    return '';
  }

  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;
  const duration = [];

  if (hours > 0) {
    duration.push(`${hours} hr${hours > 1 ? 's' : ''}`);
  }

  if (remainingMinutes > 0 || hours === 0) {
    duration.push(`${remainingMinutes} min`);
  }

  return duration.join(' ');
}

function formatDurationRangeValue(minutes) {
  return formatDuration(minutes) || '0 min';
}

function formatCookTime(hit) {
  const indexedMinutes = Number(hit.cook_time_minutes);
  const minutes =
    Number.isFinite(indexedMinutes) && indexedMinutes > 0
      ? indexedMinutes
      : parseCookTimeMinutes(hit.total_time);

  return formatDuration(minutes) || hit.total_time;
}

function usePositiveNumericMinimum(attribute, filters) {
  const [minimum, setMinimum] = useState(null);

  useEffect(() => {
    let isCurrent = true;

    searchClient
      .search([
        {
          indexName,
          params: {
            filters,
            facets: [attribute],
            hitsPerPage: 0
          }
        }
      ])
      .then(({ results }) => {
        if (!isCurrent) {
          return;
        }

        const stats = results[0] && results[0].facets_stats;
        const minimumValue =
          stats && stats[attribute] ? Number(stats[attribute].min) : null;

        if (Number.isFinite(minimumValue) && minimumValue > 0) {
          setMinimum(minimumValue);
        }
      })
      .catch(() => {
        if (isCurrent) {
          setMinimum(null);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [attribute, filters]);

  return minimum;
}

function RecipeArchiveHit({ hit }) {
  const category = Array.isArray(hit.recipe_categories)
    ? hit.recipe_categories[0]
    : hit.taxonomy;
  const recipeId = hit.post_id || hit.objectID || '';
  const cookTime = formatCookTime(hit);

  return (
    <article className="card card--recipe">
      <a
        href={hit.permalink}
        className="module-hover-root card__link js-ab-recipe-card"
        data-recipe-id={recipeId}
        data-recipe-title={hit.post_title}
        data-recipe-url={hit.permalink}
      >
        <section className="card__header">
          <div className="card__media">
            <img src={hit.post_image} alt={hit.post_title} />
          </div>
        </section>

        <section className="card__body">
          {category && <p className="card__taxonomy">{decodeHtml(category)}</p>}
          <h4 className="card__title">{hit.post_title}</h4>
          <section className="card__footer">
            <div className="grid grid--card grid--reverse grid--middle">
              <div className="grid__col-xs-12 grid__col-md-6 u--margin-top-10-max-sm u--margin-bottom-0"></div>
              <div className="grid__col-xs-12 grid__col-md-6">
                {cookTime && (
                  <div className="card__time">
                    <i className="icon-clock-time"></i>
                    {cookTime}
                  </div>
                )}
              </div>
            </div>
          </section>
        </section>
      </a>
    </article>
  );
}

function FilterSection({ title, field, children }) {
  return (
    <section
      className="recipe-archive-search__filter-section"
      data-ab-filter-field={field}
    >
      <h3 className="recipe-archive-search__filter-title">{title}</h3>
      {children}
    </section>
  );
}

const HiddenCookingMethods = ['Charcoal Grilling'];

const transformCookingMethodItems = (items) =>
  items.filter((item) => !HiddenCookingMethods.includes(item.label));

function RangeSlider({ attribute, valueLabel, formatValue, minimumValue }) {
  const { start, range, canRefine, refine } = useRange({ attribute });
  const rawRangeMin = Number.isFinite(range.min) ? range.min : 0;
  const rangeMax = Number.isFinite(range.max) ? range.max : 0;
  const positiveMinimum = Number(minimumValue);
  const rangeMin =
    Number.isFinite(positiveMinimum) && positiveMinimum > 0 && rawRangeMin <= 0
      ? positiveMinimum
      : rawRangeMin;
  const inputMax = Math.max(rangeMax, rangeMin);
  const currentMax = Math.max(
    Number.isFinite(start[1]) ? start[1] : inputMax,
    rangeMin
  );

  const formatLabel = (value) =>
    formatValue ? formatValue(value) : `${Math.round(value)}`;

  const onChange = (event) => {
    const nextMax = Math.max(Number(event.currentTarget.value), rangeMin);
    refine([rangeMin, nextMax]);
  };

  return (
    <div className="recipe-archive-search__slider">
      <div className="recipe-archive-search__slider-values">
        <span>
          {valueLabel} {formatLabel(currentMax)}
        </span>
      </div>
      <div className="recipe-archive-search__slider-track">
        <input
          type="range"
          className="recipe-archive-search__slider-input js-ab-recipe-filter-control"
          min={rangeMin}
          max={inputMax}
          value={currentMax}
          onChange={onChange}
          disabled={!canRefine}
          aria-label={`${attribute} maximum`}
        />
      </div>
    </div>
  );
}

function InfiniteResults({ hitComponent: HitComponent }) {
  const { hits, showMore, isLastPage } = useInfiniteHits();
  const sentinelRef = useRef(null);
  const loadingRef = useRef(false);
  const showMoreRef = useRef(showMore);

  useEffect(() => {
    showMoreRef.current = showMore;
  }, [showMore]);

  useEffect(() => {
    loadingRef.current = false;
  }, [hits]);

  useEffect(() => {
    if (isLastPage || !sentinelRef.current) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !loadingRef.current) {
          loadingRef.current = true;
          showMoreRef.current();
        }
      },
      { rootMargin: '600px 0px' }
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, [isLastPage]);

  return (
    <div className="recipe-archive-search__hits">
      <ol className="recipe-archive-search__hits-list">
        {hits.map(hit => (
          <li
            key={hit.objectID}
            className="recipe-archive-search__hits-item grid__recipe grid__col-xs-12 grid__col-md-6 grid__col-lg-4"
          >
            <HitComponent hit={hit} />
          </li>
        ))}
      </ol>
      {!isLastPage && (
        <div
          ref={sentinelRef}
          className="recipe-archive-search__load-more-sentinel"
          aria-hidden="true"
        />
      )}
    </div>
  );
}

export default function RecipeArchiveSearch() {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const minimumCookTime = usePositiveNumericMinimum(
    'cook_time_minutes',
    'post_type:recipe AND cook_time_minutes > 0'
  );

  return (
    <InstantSearch
      indexName={indexName}
      searchClient={searchClient}
      routing={routing}
    >
      <Configure
        filters="post_type:recipe"
        hitsPerPage={12}
        facetingAfterDistinct={true}
      />

      <section className="section recipe-archive-search">
        <div className="container">
          <div className="recipe-archive-search__toolbar">
            <div className="recipe-archive-search__search">
              <SearchBox
                placeholder="Search for Beef Recipes"
                searchAsYouType={false}
                queryHook={queryHook}
                submitTitle="Search"
                resetTitle="Clear Search"
                classNames={{
                  root: 'recipe-archive-search__searchbox',
                  form: 'recipe-archive-search__searchform',
                  input: 'form__input recipe-archive-search__searchinput',
                  submit:
                    'btn btn--primary recipe-archive-search__searchsubmit',
                  reset: 'recipe-archive-search__searchreset',
                  submitIcon: 'recipe-archive-search__searchsubmit-icon',
                  loadingIcon: 'recipe-archive-search__searchloading-icon'
                }}
              />
            </div>
            <div className="recipe-archive-search__summary recipe-archive-search__summary--desktop">
              <Stats
                classNames={{ root: 'recipe-archive-search__stats' }}
                translations={{
                  rootElementText({ nbHits }) {
                    return `${nbHits} Results`;
                  }
                }}
              />
              <SortBy
                defaultRefinement={indexName}
                items={[
                  { value: indexName, label: 'Relevance' },
                  { value: `${indexName}_popular`, label: 'Popular' },
                  { value: `${indexName}_newest`, label: 'Newest' },
                  { value: `${indexName}_oldest`, label: 'Oldest' }
                ]}
                classNames={{
                  select: 'js-ab-recipe-sort-select'
                }}
              />
            </div>
          </div>

          <div
            className={`recipe-archive-search__mobile-filters${
              isFiltersOpen
                ? ' recipe-archive-search__mobile-filters--open'
                : ''
            }`}
          >
            <button
              type="button"
              className="btn btn--secondary recipe-archive-search__filters-toggle"
              aria-expanded={isFiltersOpen}
              aria-controls="recipe-archive-search-sidebar"
              onClick={() => setIsFiltersOpen(!isFiltersOpen)}
              aria-label={isFiltersOpen ? 'Close Filters' : 'Open Filters'}
            >
              {isFiltersOpen ? (
                <span className="recipe-archive-search__filters-toggle-icon">
                  &times;
                </span>
              ) : (
                'Filters'
              )}
            </button>
          </div>

          <div className="recipe-archive-search__layout">
            <aside
              id="recipe-archive-search-sidebar"
              className={`recipe-archive-search__sidebar${
                isFiltersOpen ? ' recipe-archive-search__sidebar--open' : ''
              }`}
            >
              <div className="recipe-archive-search__sidebar-header">
                <h2 className="recipe-archive-search__sidebar-title">Filter</h2>
                <div className="recipe-archive-search__sidebar-actions">
                  <ClearRefinements
                    translations={{ resetButtonText: 'Clear' }}
                    classNames={{
                      root: 'recipe-archive-search__clear',
                      button:
                        'btn btn--secondary btn--sm js-ab-recipe-filter-clear'
                    }}
                  />
                </div>
              </div>

              <FilterSection title="Category" field="recipe_categories">
                <RefinementList
                  attribute="recipe_categories"
                  sortBy={['name:asc']}
                  limit={100}
                  classNames={{
                    root: 'recipe-archive-search__refinement',
                    list: 'recipe-archive-search__refinement-list',
                    item: 'recipe-archive-search__refinement-item',
                    label: 'recipe-archive-search__refinement-label',
                    checkbox:
                      'recipe-archive-search__refinement-checkbox js-ab-recipe-filter-control',
                    count: 'recipe-archive-search__refinement-count'
                  }}
                />
              </FilterSection>

              <FilterSection title="Cooking Methods" field="cooking_methods">
                <RefinementList
                  attribute="cooking_methods"
                  transformItems={transformCookingMethodItems}
                  sortBy={['name:asc']}
                  limit={100}
                  classNames={{
                    root: 'recipe-archive-search__refinement',
                    list: 'recipe-archive-search__refinement-list',
                    item: 'recipe-archive-search__refinement-item',
                    label: 'recipe-archive-search__refinement-label',
                    checkbox:
                      'recipe-archive-search__refinement-checkbox js-ab-recipe-filter-control',
                    count: 'recipe-archive-search__refinement-count'
                  }}
                />
              </FilterSection>

              <FilterSection title="Cook Time" field="cook_time_minutes">
                <RangeSlider
                  attribute="cook_time_minutes"
                  valueLabel="Max Time:"
                  formatValue={formatDurationRangeValue}
                  minimumValue={minimumCookTime || 1}
                />
              </FilterSection>

              <FilterSection
                title="Number of Ingredients"
                field="ingredient_count"
              >
                <RangeSlider
                  attribute="ingredient_count"
                  valueLabel="Max Ingredients:"
                />
              </FilterSection>
            </aside>

            <div className="recipe-archive-search__results">
              <div className="recipe-archive-search__summary recipe-archive-search__summary--mobile">
                <Stats
                  classNames={{ root: 'recipe-archive-search__stats' }}
                  translations={{
                    rootElementText({ nbHits }) {
                      return `${nbHits} Results`;
                    }
                  }}
                />
                <SortBy
                  defaultRefinement={indexName}
                  items={[
                    { value: indexName, label: 'Relevance' },
                    { value: `${indexName}_popular`, label: 'Popular' },
                    { value: `${indexName}_newest`, label: 'Newest' },
                    { value: `${indexName}_oldest`, label: 'Oldest' }
                  ]}
                  classNames={{
                    select: 'js-ab-recipe-sort-select'
                  }}
                />
              </div>
              <InfiniteResults hitComponent={RecipeArchiveHit} />
            </div>
          </div>
        </div>
      </section>
    </InstantSearch>
  );
}
